<template>
  <div class="auth-wrapper kabayan-auth-wrapper">
    <!-- snackbar -->
    <c-snackbar
      v-model="snackbar.isVisible"
      :message="snackbar.message"
      :type="snackbar.type"
    ></c-snackbar>

    <div class="kabayan-auth-container">
      <!-- Left Hero Section (60%) -->
      <div class="hero-section d-none d-lg-flex">
        <!-- Floating shapes background -->
        <div class="glow-sphere sphere-1"></div>
        <div class="glow-sphere sphere-2"></div>

        <div class="hero-overlay"></div>

        <!-- Hero content wrapper -->
        <div class="hero-content">
          <div class="hero-header fade-in">
            <p class="hero-subtitle font-weight-medium">
              Kabayan Rewards
            </p>
          </div>

          <!-- Generated Illustration Container -->
          <div class="illustration-container fade-in-up">
            <img
              src="@/assets/images/misc/rewards_hero.png"
              alt="Kabayan Rewards"
              class="hero-illustration floating"
            />
          </div>

          <!-- Feature Cards -->
          <div class="feature-cards-grid fade-in-up-delayed">
            <div class="feature-card glassmorphic">
              <div class="feature-icon-wrapper">
                <v-icon size="24" color="white">{{ icons.mdiGiftOutline }}</v-icon>
              </div>
              <div class="feature-text">
                <h3 class="feature-title">Earn Reward Points</h3>
                <p class="feature-desc">Collect points on every purchase at Kabayan Hypermarket.</p>
              </div>
            </div>

            <div class="feature-card glassmorphic">
              <div class="feature-icon-wrapper">
                <v-icon size="24" color="white">{{ icons.mdiCrownOutline }}</v-icon>
              </div>
              <div class="feature-text">
                <h3 class="feature-title">Exclusive Member Benefits</h3>
                <p class="feature-desc">Unlock special discounts, deals, and early access.</p>
              </div>
            </div>

            <div class="feature-card glassmorphic">
              <div class="feature-icon-wrapper">
                <v-icon size="24" color="white">{{ icons.mdiShieldCheckOutline }}</v-icon>
              </div>
              <div class="feature-text">
                <h3 class="feature-title">Fast & Secure Experience</h3>
                <p class="feature-desc">Your loyalty points and data are completely secure with us.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Login Section (40%) -->
      <div class="login-section">
        <!-- Floating shapes in login side for mobile/tablet visual interest -->
        <div class="login-glow-sphere d-lg-none"></div>

        <div class="login-card-container">
          <div class="login-card glassmorphic-card fade-in">
            <!-- Login Header (Logos removed) -->
            <div class="login-header mb-6 text-center">
              <h2 class="login-title font-weight-bold mb-1">Welcome Back</h2>
              <p class="login-subtitle">Sign in to continue to Kabayan Rewards.</p>
            </div>

            <!-- Form -->
            <v-form
              ref="form"
              v-model="valid"
              @submit.prevent="onSubmit"
            >
              <!-- Error Banner -->
              <v-alert
                v-if="errorMessage"
                type="error"
                text
                class="mb-6 rounded-lg text-sm"
                dense
                dismissible
                @input="errorMessage = null"
              >
                {{ errorMessage }}
              </v-alert>

              <!-- Email Field -->
              <div class="form-group mb-6">
                <label class="input-label mb-2 d-block">Email Address</label>
                <v-text-field
                  v-model="formData.email"
                  outlined
                  placeholder="Enter your email"
                  hide-details="auto"
                  :rules="[validators.required, validators.emailValidator]"
                  :error-messages="errors.email"
                  @change="errors.email = []"
                  class="custom-input"
                ></v-text-field>
              </div>

              <!-- Password Field -->
              <div class="form-group mb-4">
                <label class="input-label mb-2 d-block">Password</label>
                <v-text-field
                  v-model="formData.password"
                  outlined
                  placeholder="Enter your password"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                  hide-details="auto"
                  :rules="[validators.passwordValidator]"
                  :error-messages="errors.password"
                  @click:append="isPasswordVisible = !isPasswordVisible"
                  @change="errors.password = []"
                  class="custom-input"
                ></v-text-field>
              </div>

              <!-- Remember Me & Forgot Password -->
              <div class="d-flex align-center justify-space-between mb-8 flex-wrap">
                <v-checkbox
                  v-model="formData.rememberMe"
                  label="Remember Me"
                  hide-details
                  class="remember-checkbox mt-0 pt-0"
                  color="primary"
                ></v-checkbox>

                <router-link
                  :to="{ name: 'auth.forgot-password' }"
                  class="forgot-password-link text-sm font-weight-medium"
                >
                  Forgot Password?
                </router-link>
              </div>

              <!-- Sign In Button -->
              <v-btn
                block
                color="primary"
                type="submit"
                x-large
                class="submit-btn font-weight-bold"
                :loading="isLoading"
                depressed
              >
                Sign In
              </v-btn>
            </v-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
	mdiEyeOutline,
	mdiEyeOffOutline,
	mdiGiftOutline,
	mdiCrownOutline,
	mdiShieldCheckOutline,
} from '@mdi/js'
import themeConfig from '@themeConfig'
import { required, emailValidator, passwordValidator } from '@core/utils/validation'

export default {
	data() {
		return {
			formData: {
				email: '',
				password: '',
				rememberMe: false,
			},
			valid: false,
			errors: {},
			errorMessage: null,
			appName: themeConfig.app.name,
			appLogo: themeConfig.app.logo,
			icons: {
				mdiEyeOutline,
				mdiEyeOffOutline,
				mdiGiftOutline,
				mdiCrownOutline,
				mdiShieldCheckOutline,
			},
			isPasswordVisible: false,
			validators: { required, emailValidator, passwordValidator },
			snackbar: {
				isVisible: false,
				message: '',
				type: 'info',
			},
			isLoading: false,
		}
	},
	methods: {
		onSubmit() {
			if (this.valid) {
				this.isLoading = true
				this.errorMessage = ''
				this.$store
					.dispatch('auth/login', this.formData)
					.then(() => {
						this.isLoading = false
						this.$router.push({ name: 'home' }).catch(() => {})
					})
					.catch(error => {
						this.isLoading = false
						const { errors, message } = error.response.data
						if (errors) {
							this.$set(this, 'errors', errors)
							this.$set(this, 'valid', false)
						}
						this.errorMessage = message
						this.snackbar.message = message || 'Login failed. Please check your credentials.'
						this.snackbar.type = 'error'
						this.snackbar.isVisible = true
					})
			} else {
				this.$refs.form.validate()
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import '@core/preset/preset/pages/auth.scss';

.kabayan-auth-wrapper {
  background: linear-gradient(
    135deg,
    #0b2240 0%,
    #1f4978 50%,
    var(--v-primary-base) 100%
  ) !important;
  display: flex;
  min-height: 100vh;
  width: 100%;
  align-items: stretch;
  justify-content: center;
  overflow-x: hidden;
  position: relative;
  font-family: 'Inter', 'Outfit', sans-serif;
}

.kabayan-auth-container {
  display: flex;
  width: 100%;
  min-height: 100vh;
  position: relative;
  z-index: 2;
}

// Left Section
.hero-section {
  flex: 0 0 60%;
  max-width: 60%;
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 4rem;
  overflow: hidden; // Disabled scrolling as requested
  border-right: 1px solid rgba(255, 255, 255, 0.08);

  .hero-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 20% 30%, rgba(#1f4978, 0.4) 0%, rgba(#0b2240, 0.85) 100%);
    z-index: 1;
  }

  // Floating shape/glow effects
  .glow-sphere {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.35;
    z-index: 0;
    pointer-events: none;

    &.sphere-1 {
      width: 400px;
      height: 400px;
      background: var(--v-primary-base);
      top: -100px;
      left: -100px;
      animation: float-slow 15s infinite ease-in-out alternate;
    }

    &.sphere-2 {
      width: 500px;
      height: 500px;
      background: #dfa841; // Golden accent glow
      bottom: -150px;
      right: -50px;
      opacity: 0.15;
      animation: float-slow 20s infinite ease-in-out alternate-reverse;
    }
  }

  .hero-content {
    position: relative;
    z-index: 2;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 2rem;
  }

  .hero-header {
    .hero-subtitle {
      font-size: 1.35rem; // Main hero text font size
      font-weight: 600 !important;
      color: #ffffff;
      max-width: 520px;
      line-height: 1.5;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  .illustration-container {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 38vh;
    margin: 1rem 0;

    .hero-illustration {
      max-width: 65%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 20px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  // Feature Cards Layout
  .feature-cards-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
    margin-top: auto;
  }

  .feature-card {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
    border-radius: 16px;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:hover {
      transform: translateY(-5px);
      background: rgba(255, 255, 255, 0.1) !important;
      border-color: rgba(255, 255, 255, 0.25) !important;
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
    }

    .feature-icon-wrapper {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.1);
    }

    .feature-text {
      .feature-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 0.25rem;
      }
      .feature-desc {
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.65);
        line-height: 1.4;
        margin-bottom: 0;
      }
    }
  }
}

// Right Section
.login-section {
  flex: 0 0 40%;
  max-width: 40%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  position: relative;

  .login-glow-sphere {
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: var(--v-primary-base);
    filter: blur(100px);
    opacity: 0.2;
    top: 10%;
    right: -100px;
    pointer-events: none;
  }
}

.login-card-container {
  width: 100%;
  max-width: 450px;
  z-index: 2;
}

// Glassmorphism effects
.glassmorphic {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.glassmorphic-card {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  color: #1e293b;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 30px 60px -10px rgba(0, 0, 0, 0.3);
  }
}

// Form elements styles
.login-title {
  font-size: 1.45rem; // Reduced to medium size as requested
  color: #0f172a;
  letter-spacing: -0.3px;
}

.login-subtitle {
  color: #64748b;
  font-size: 0.875rem;
}

.input-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.2px;
}

// Styling Vuetify Text Inputs to look ultra modern
::v-deep .custom-input {
  .v-input__control {
    .v-input__slot {
      background: #f8fafc !important;
      border-radius: 12px !important;
      border: 1px solid #e2e8f0 !important;
      transition: all 0.2s ease-in-out;
      box-shadow: none !important;

      fieldset {
        border: none !important;
      }

      &:hover {
        background: #f1f5f9 !important;
        border-color: #cbd5e1 !important;
      }
    }
  }

  &.v-input--is-focused {
    .v-input__control {
      .v-input__slot {
        background: #ffffff !important;
        border-color: var(--v-primary-base) !important;
        box-shadow: 0 0 0 4px rgba(#396EB0, 0.15) !important;
      }
    }
  }

  // Error styling
  &.v-input--has-state.error--text {
    .v-input__control {
      .v-input__slot {
        border-color: var(--v-error-base) !important;
        box-shadow: 0 0 0 4px rgba(#FF4C51, 0.15) !important;
      }
    }
  }
}

// Checkbox custom styles
::v-deep .remember-checkbox {
  .v-icon {
    font-size: 20px;
  }
  .v-label {
    font-size: 0.875rem;
    color: #475569;
    font-weight: 500;
  }
}

.forgot-password-link {
  color: var(--v-primary-base);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #1f4978;
    text-decoration: underline;
  }
}

.submit-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-size: 1rem !important;
  letter-spacing: 0px !important;
  height: 52px !important;
  box-shadow: 0 4px 12px rgba(#396EB0, 0.35) !important;
  transition: all 0.2s ease !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(#396EB0, 0.45) !important;
  }

  &:active {
    transform: translateY(0);
  }
}

// Keyframe animations
@keyframes float-slow {
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
  100% { transform: translateY(0px) rotate(0deg); }
}

@keyframes floating {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
  100% { transform: translateY(0px); }
}

.floating {
  animation: floating 6s ease-in-out infinite;
}

// Animations classes
.fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

.fade-in-up {
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.fade-in-up-delayed {
  opacity: 0;
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive design overrides
@media (max-width: 1264px) {
  .hero-section {
    display: none !important;
  }

  .login-section {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    background: linear-gradient(
      135deg,
      #0b2240 0%,
      #1f4978 50%,
      var(--v-primary-base) 100%
    ) !important;
  }

  .glassmorphic-card {
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
}

@media (max-width: 480px) {
  .login-card-container {
    padding: 0 1rem;
  }

  .glassmorphic-card {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }

  .login-title {
    font-size: 1.3rem;
  }
}
</style>
